import { useMemo } from 'react';
import { SnapshotData, OwnedTargetInfo } from 'ais-facade-api/api';
import { getDimensions } from 'app/utils/utils';
import i18n from 'resources/i18n';

export const useVesselCharacteristics = (vessel: OwnedTargetInfo | SnapshotData | undefined) => {
  const lengthDimension = useMemo(() => {
    if (!vessel) {
      return undefined;
    }
    let dimension;
    if ('staticData' in vessel && vessel.staticData?.dimensions) {
      dimension = getDimensions(vessel.staticData?.dimensions).toBowStern;
    }
    if ('registryData' in vessel && vessel.registryData?.length) {
      dimension = Math.round(vessel.registryData?.length);
    }
    return typeof dimension === 'number' ? `${dimension} ${i18n.t('MeterUnit')}` : undefined;
  }, [vessel]);

  const widthDimension: string | undefined = useMemo(() => {
    if (!vessel) {
      return undefined;
    }
    let dimension;
    if ('staticData' in vessel && vessel.staticData?.dimensions) {
      dimension = getDimensions(vessel.staticData?.dimensions).toPortStarboard;
    }
    if ('registryData' in vessel && vessel.registryData?.width) {
      dimension = Math.round(vessel.registryData?.width);
    }
    return typeof dimension === 'number' ? `${dimension} ${i18n.t('MeterUnit')}` : undefined;
  }, [vessel]);

  const withDraught: string | undefined = useMemo(() => {
    if (!vessel) {
      return undefined;
    }

    let draught;

    if ('voyage' in vessel && vessel.voyage) {
      draught = vessel.voyage.draught;
    }
    return typeof draught === 'number' ? `${draught} ${i18n.t('MeterUnit')}` : undefined;
  }, [vessel]);

  const withDepth: string | undefined = useMemo(() => {
    if (!vessel) {
      return undefined;
    }

    let depth;

    if ('registryData' in vessel && vessel.registryData) {
      depth = vessel.registryData.depth;
    }

    return typeof depth === 'number' ? `${depth} ${i18n.t('MeterUnit')}` : undefined;
  }, [vessel]);

  const withMaximumDraft: string | undefined = useMemo(() => {
    if (!vessel) {
      return undefined;
    }

    let maximumDraft;

    if ('registryData' in vessel && vessel.registryData) {
      maximumDraft = vessel.registryData.maximumDraft;
    }

    return typeof maximumDraft === 'number' ? `${maximumDraft} ${i18n.t('MeterUnit')}` : undefined;
  }, [vessel]);

  const withFuelBunker: string | undefined = useMemo(() => {
    if (!vessel) {
      return undefined;
    }

    let fuelBunker;

    if ('registryData' in vessel && vessel.registryData) {
      fuelBunker = vessel.registryData.fuelBunker;
    }

    return typeof fuelBunker === 'number' ? `${fuelBunker} ${i18n.t('WeightUnit')}` : undefined;
  }, [vessel]);

  const withDisplacement: string | undefined = useMemo(() => {
    if (!vessel) {
      return undefined;
    }

    let displacement;

    if ('registryData' in vessel && vessel.registryData) {
      displacement = vessel.registryData.displacement;
    }

    return typeof displacement === 'number' ? `${displacement} ${i18n.t('WeightUnit')}` : undefined;
  }, [vessel]);

  const withDeadWeight: string | undefined = useMemo(() => {
    if (!vessel) {
      return undefined;
    }

    let deadWeight;

    if ('registryData' in vessel && vessel.registryData) {
      deadWeight = vessel.registryData.dwt;
    }

    return typeof deadWeight === 'number' ? `${deadWeight} ${i18n.t('WeightUnit')}` : undefined;
  }, [vessel]);

  const withGrossTonnage: string | undefined = useMemo(() => {
    if (!vessel) {
      return undefined;
    }

    let grossTonnage;

    if ('registryData' in vessel && vessel.registryData) {
      grossTonnage = vessel.registryData.grt;
    }

    return typeof grossTonnage === 'number' ? `${grossTonnage} ${i18n.t('GrossTonnageUnit')}` : undefined;
  }, [vessel]);

  return {
    lengthDimension,
    widthDimension,
    withDraught,
    withDepth,
    withMaximumDraft,
    withFuelBunker,
    withDisplacement,
    withDeadWeight,
    withGrossTonnage,
  };
};
